import React, { useState } from 'react';
import InputMask from 'react-input-mask-next';
import { Loader, Home, Briefcase, Info } from 'lucide-react';
import type { Address } from './types';

interface AddressFormProps {
  title: string;
  icon: React.ReactNode;
  prefix: string;
  address: Address;
  loadingCep: boolean;
  errors: Record<string, string>;
  onChange: (field: keyof Address, value: string) => void;
  onCepBlur: (cep: string) => void;
}

function AddressForm({ title, icon, prefix, address, loadingCep, errors, onChange, onCepBlur }: AddressFormProps) {
  return (
    <div className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
        {icon}
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      </div>
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
            <div className="relative">
              <InputMask
                mask="99999-999"
                value={address.cep}
                onChange={(e) => onChange('cep', e.target.value)}
                onBlur={(e) => onCepBlur(e.target.value)}
                placeholder="00000-000"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
              />
              {loadingCep && (
                <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Número *</label>
            <input
              type="text"
              value={address.numero}
              onChange={(e) => onChange('numero', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors[`${prefix}-numero`] ? 'border-red-400 bg-red-50' : 'border-gray-300'
              }`}
            />
            {errors[`${prefix}-numero`] && (
              <p className="text-red-500 text-xs mt-1">{errors[`${prefix}-numero`]}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Logradouro *</label>
          <input
            type="text"
            value={address.logradouro}
            onChange={(e) => onChange('logradouro', e.target.value)}
            placeholder="Rua, Av., etc."
            className={`w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors[`${prefix}-logradouro`] ? 'border-red-400 bg-red-50' : 'border-gray-300'
            }`}
          />
          {errors[`${prefix}-logradouro`] && (
            <p className="text-red-500 text-xs mt-1">{errors[`${prefix}-logradouro`]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
          <input
            type="text"
            value={address.complemento}
            onChange={(e) => onChange('complemento', e.target.value)}
            placeholder={prefix === 'res' ? 'Apto, Bloco...' : 'Sala, Loja...'}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
            <input
              type="text"
              value={address.bairro}
              onChange={(e) => onChange('bairro', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Cidade *</label>
            <input
              type="text"
              value={address.cidade}
              onChange={(e) => onChange('cidade', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors[`${prefix}-cidade`] ? 'border-red-400 bg-red-50' : 'border-gray-300'
              }`}
            />
            {errors[`${prefix}-cidade`] && (
              <p className="text-red-500 text-xs mt-1">{errors[`${prefix}-cidade`]}</p>
            )}
          </div>
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">UF</label>
            <input
              type="text"
              value={address.estado}
              onChange={(e) => onChange('estado', e.target.value)}
              maxLength={2}
              placeholder="SP"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  enderecoResidencial: Address;
  enderecoComercial: Address;
  prefilled?: boolean;
  onComplete: (data: { enderecoResidencial: Address; enderecoComercial: Address }) => void;
}

export default function AddressesStep({ enderecoResidencial, enderecoComercial, prefilled, onComplete }: Props) {
  const [residential, setResidential] = useState<Address>(enderecoResidencial);
  const [commercial, setCommercial] = useState<Address>(enderecoComercial);
  const [loadingCep, setLoadingCep] = useState({ residential: false, commercial: false });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchCep = async (cep: string, type: 'residential' | 'commercial') => {
    if (cep.replace(/\D/g, '').length !== 8) return;
    setLoadingCep(prev => ({ ...prev, [type]: true }));
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep.replace(/\D/g, '')}/json/`);
      const data = await res.json();
      if (!data.erro) {
        const setter = type === 'residential' ? setResidential : setCommercial;
        setter(prev => ({
          ...prev,
          logradouro: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          estado: data.uf || '',
        }));
      }
    } catch {
      // silent
    } finally {
      setLoadingCep(prev => ({ ...prev, [type]: false }));
    }
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!residential.logradouro.trim()) errs['res-logradouro'] = 'Logradouro obrigatório';
    if (!residential.numero.trim()) errs['res-numero'] = 'Número obrigatório';
    if (!residential.cidade.trim()) errs['res-cidade'] = 'Cidade obrigatória';
    if (!commercial.logradouro.trim()) errs['com-logradouro'] = 'Logradouro obrigatório';
    if (!commercial.numero.trim()) errs['com-numero'] = 'Número obrigatório';
    if (!commercial.cidade.trim()) errs['com-cidade'] = 'Cidade obrigatória';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Endereços</h2>
        <p className="text-gray-600 text-sm">Informe o endereço residencial e comercial do cliente.</p>
      </div>

      {prefilled && (
        <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-sm">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-600" />
          <span>Endereços carregados do contrato anterior — verifique e edite se necessário.</span>
        </div>
      )}

      <AddressForm
        title="Endereço Residencial"
        icon={<Home className="w-4 h-4 text-gray-400" />}
        prefix="res"
        address={residential}
        loadingCep={loadingCep.residential}
        errors={errors}
        onChange={(field, value) => setResidential(prev => ({ ...prev, [field]: value }))}
        onCepBlur={(cep) => fetchCep(cep, 'residential')}
      />

      <AddressForm
        title="Endereço Comercial"
        icon={<Briefcase className="w-4 h-4 text-gray-400" />}
        prefix="com"
        address={commercial}
        loadingCep={loadingCep.commercial}
        errors={errors}
        onChange={(field, value) => setCommercial(prev => ({ ...prev, [field]: value }))}
        onCepBlur={(cep) => fetchCep(cep, 'commercial')}
      />

      <button
        onClick={() => {
          if (validate()) onComplete({ enderecoResidencial: residential, enderecoComercial: commercial });
        }}
        className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-base hover:bg-blue-700 transition-colors shadow-sm"
      >
        Continuar
      </button>
    </div>
  );
}
